// 能源 API 的假資料（WP-6 API 上 dev 之前看畫面用；e2e/fixtures/energyWorld.ts 也直接用這份，畫面與測試同一個來源）。
// 純函式、沒有瀏覽器相依：energyMock(world, method, path, query, body, now) → { status, body }。
// 形狀照 types.ts（= plan SPEC #energy-api，VMS 端的假設；WP-6 定案後 api.ts 轉換）。
// 世界：每個{leaf}一台 Kinio Station（8 孔，前 6 孔接攝影機、第 7 孔沒對到設備）+ 招牌燈、飲水機兩個插座；
// 時區 Asia/Shanghai 的{leaf}用 CN¥ 與中國係數（只有 CO2）；用 home_id 雜湊決定每台的功率形狀，所以每次算都一樣。
import type {
  Attention,
  Carbon,
  DeviceEnergy,
  EnergySettings,
  EnergySummary,
  HomeRow,
  MeterKind,
  MonthReport,
  Money,
  OrgEnergySettings,
  OrgRow,
  Period,
  PowerModeResult,
  RankRow,
  SeriesPoint,
  Tariff,
  TariffTemplate,
} from './types';

export interface MockHomeIn {
  home_id: string;
  name: string;
  org_id: string;
  org_name?: string;
  site_id?: string;
  path?: string;
  tz?: string;
  /** owner / admin 才能改用電設定 */
  role?: string;
}

export interface MockOrgIn {
  org_id: string;
  name: string;
  role?: string;
}

interface Meter {
  key: string;
  kind: MeterKind;
  name: string;
  device_ref: string;
  station_ref?: string;
  station_name?: string;
  port?: number;
  /** 平均功率（W）與形狀 */
  base: number;
  night: number;
  /** 攝影機（可設功耗模式） */
  camera?: boolean;
  power_mode?: number;
  power_mode_changed_at?: string;
  /** 讀不到（從 now - gapHours 起沒有資料） */
  gapHours?: number;
}

export interface EnergyMockWorld {
  homes: MockHomeIn[];
  orgs: MockOrgIn[];
  /** 組織電價方案、{leaf}用電設定、組織設定（可寫） */
  tariffs: Tariff[];
  settings: Record<string, EnergySettings>;
  orgSettings: Record<string, OrgEnergySettings>;
  /** 功耗模式的套用（device_ref → 套用時間、模式；pending 到 deadline 前） */
  powerModes: Record<string, { mode: number; at: number; result: 'pending' | 'applied' | 'failed' }>;
  /** 讀不到整個{leaf}（e2e 情境開關） */
  unavailableHomes: string[];
  /** 沒有量測點的{leaf} */
  emptyHomes: string[];
  seq: number;
}

const HOUR = 3600_000;
const DAY = 24 * HOUR;

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967295;
}

export function energyMockWorld(homes: MockHomeIn[], orgs: MockOrgIn[]): EnergyMockWorld {
  const w: EnergyMockWorld = { homes, orgs, tariffs: [], settings: {}, orgSettings: {}, powerModes: {}, unavailableHomes: [], emptyHomes: [], seq: 0 };
  for (const o of orgs) {
    const cny = homes.some((h) => h.org_id === o.org_id && isCN(h));
    w.orgSettings[o.org_id] = { org_id: o.org_id, show_carbon: true, carbon_category: 'grid', version: 1 };
    const t1: Tariff = {
      tariff_id: `trf-${o.org_id}-1`,
      org_id: o.org_id,
      name: '台電表燈營業用',
      kind: 'tiered',
      currency: 'TWD',
      template_id: 'tw-taipower-commercial',
      versions: [{ version: 1, effective_from: '2026-04-01', rates: { tiers: [{ upto_kwh: 330, price: 3.42 }, { upto_kwh: 700, price: 4.48 }, { upto_kwh: null, price: 6.18 }] } }],
      applied_homes: [],
      version: 1,
    };
    w.tariffs.push(t1);
    if (cny)
      w.tariffs.push({
        tariff_id: `trf-${o.org_id}-cn`,
        org_id: o.org_id,
        name: '廈門一般工商業',
        kind: 'flat',
        currency: 'CNY',
        versions: [{ version: 1, effective_from: '2026-01-01', rates: { flat: 0.68 } }],
        applied_homes: [],
        version: 1,
      });
  }
  homes.forEach((h, i) => {
    // 每 7 個{leaf}有一個沒設電價
    const noTariff = i % 7 === 6;
    const t = w.tariffs.find((x) => x.org_id === h.org_id && x.currency === (isCN(h) ? 'CNY' : 'TWD'));
    if (t && !noTariff) t.applied_homes.push(h.home_id);
    w.settings[h.home_id] = {
      home_id: h.home_id,
      tariff_id: noTariff || !t ? null : t.tariff_id,
      tariff_source: noTariff || !t ? 'none' : 'org',
      tariff_name: noTariff || !t ? undefined : t.name,
      carbon_category: 'grid',
      alerts: { poe_warn_pct: 80, poe_high_pct: 95, gap_hours: 2 },
      managed_by_template: null,
      can_edit: h.role === 'owner' || h.role === 'admin',
      version: 1,
    };
  });
  return w;
}

function isCN(h: MockHomeIn): boolean {
  return h.tz === 'Asia/Shanghai';
}

const CAMS = ['門口攝影機', '騎樓攝影機', '收銀台攝影機', '倉庫攝影機', '後門攝影機', '走道攝影機'];


/** 假世界裡一個{leaf}的設備編號（e2e 的 me/devices 用同一組，設備頁才對得上）。 */
export function mockRefs(homeId: string): { station: string; cams: string[]; plugs: [string, string] } {
  const tail = (n: number) => homeId.replace(/[^0-9A-Za-z]/g, '').slice(-n).toUpperCase().padStart(n, '0');
  const p = homeId.slice(-6).toUpperCase();
  return { station: `KST-${tail(8)}`, cams: CAMS.map((_, i) => `CAM-${tail(6)}${i}`), plugs: [`PON-${p}1`, `PON-${p}2`] };
}

export const MOCK_CAM_NAMES = CAMS;

function metersOf(w: EnergyMockWorld, h: MockHomeIn): Meter[] {
  if (w.emptyHomes.includes(h.home_id)) return [];
  const r = hash(h.home_id);
  const refs = mockRefs(h.home_id);
  const st = refs.station;
  const out: Meter[] = [];
  CAMS.forEach((name, i) => {
    const ref = refs.cams[i];
    const pm = w.powerModes[ref];
    out.push({
      key: `${st}#poe${i + 1}`,
      kind: 'poe',
      name,
      device_ref: ref,
      station_ref: st,
      station_name: 'Station A',
      port: i + 1,
      base: 4.5 + ((r * 10 + i) % 3),
      night: i === 1 ? 12 : 6.5 + ((r * 7 + i) % 2),
      camera: true,
      power_mode: pm?.result === 'applied' ? pm.mode : i === 2 ? 0 : 1,
      power_mode_changed_at: pm?.result === 'applied' ? new Date(pm.at).toISOString() : undefined,
    });
  });
  out.push({ key: `${st}#poe7`, kind: 'poe', name: '', device_ref: '', station_ref: st, station_name: 'Station A', port: 7, base: 3.1, night: 3.1 });
  out.push({ key: refs.plugs[0], kind: 'plug', name: '招牌燈插座', device_ref: refs.plugs[0], base: 2, night: 200 });
  out.push({
    key: refs.plugs[1],
    kind: 'plug',
    name: '飲水機插座',
    device_ref: refs.plugs[1],
    base: 55 + r * 20,
    night: 40,
    gapHours: r > 0.85 ? 3 : undefined,
  });
  return out;
}

/** 某{leaf}時區的當地時間位移（分鐘）。 */
const tzFmt = new Map<string, Intl.DateTimeFormat>();
const tzOff = new Map<string, number>();
function tzOffsetMin(tz: string | undefined, at: number): number {
  if (!tz) return 480;
  // 同一個時區同一天的位移快取（mock 一次要算上萬個區間）
  const ck = `${tz}|${Math.floor(at / DAY)}`;
  const hit = tzOff.get(ck);
  if (hit !== undefined) return hit;
  try {
    let f = tzFmt.get(tz);
    if (!f) tzFmt.set(tz, (f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' })));
    const p = Object.fromEntries(f.formatToParts(new Date(at)).map((x) => [x.type, x.value]));
    const local = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
    const v = Math.round((local - Math.floor(at / 60_000) * 60_000) / 60_000);
    tzOff.set(ck, v);
    return v;
  } catch {
    return 480;
  }
}

/** 一個量測點某一小時的平均功率（W）；讀不到回 null。 */
function wattsAt(m: Meter, localHour: number, dayIdx: number, hourStart: number, now: number): number | null {
  if (m.gapHours && hourStart >= now - m.gapHours * HOUR) return null;
  const night = localHour >= 18 || localHour < 6;
  let w = night ? m.night : m.base;
  // 招牌燈只在 18–24 時開
  if (m.kind === 'plug' && m.night === 200) w = localHour >= 18 ? 200 : 1.2;
  // 週末低一點、每天小起伏
  const wobble = 0.9 + ((dayIdx * 37 + localHour * 11) % 20) / 100;
  if (m.power_mode === 2 && m.camera) w *= 0.8;
  return +(w * wobble).toFixed(3);
}

interface Span {
  from: number;
  to: number;
}

/** 一個量測點在 [from, to) 的用電（mWh）與缺的小時數。 */
function meterSpan(m: Meter, tz: string | undefined, s: Span, now: number): { mwh: number; missing: number; max: number; hours: number } {
  let mwh = 0;
  let missing = 0;
  let max = 0;
  let hours = 0;
  const off = tzOffsetMin(tz, s.from) * 60_000;
  for (let t = s.from; t < Math.min(s.to, now); t += HOUR) {
    const local = new Date(t + off);
    const frac = Math.min(1, (Math.min(s.to, now) - t) / HOUR);
    const w = wattsAt(m, local.getUTCHours(), Math.floor((t + off) / DAY), t, now);
    hours += frac;
    if (w === null) {
      missing += frac;
      continue;
    }
    mwh += w * 1000 * frac;
    max = Math.max(max, w);
  }
  return { mwh: Math.round(mwh), missing: Math.round(missing), max, hours };
}

function localDate(at: number, tz?: string): { y: number; m: number; d: number } {
  const t = new Date(at + tzOffsetMin(tz, at) * 60_000);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

function ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** 本期的起訖（UTC 毫秒），依{leaf}時區；period day = 今天、month = 本月 1 日起、year = 今年 1 月起、week = 本週一起。 */
function periodSpan(period: Period, date: string | null, tz: string | undefined, now: number): Span & { prev: Span; days: number } {
  const ld = date ? { y: +date.slice(0, 4), m: +date.slice(5, 7), d: +date.slice(8, 10) } : localDate(now, tz);
  const off = tzOffsetMin(tz, now) * 60_000;
  const at = (y: number, m: number, d: number) => Date.UTC(y, m - 1, d) - off;
  let from: number;
  let end: number;
  let prevFrom: number;
  if (period === 'day') {
    from = at(ld.y, ld.m, ld.d);
    end = from + DAY;
    prevFrom = from - DAY;
  } else if (period === 'week') {
    const wd = (new Date(Date.UTC(ld.y, ld.m - 1, ld.d)).getUTCDay() + 6) % 7;
    from = at(ld.y, ld.m, ld.d) - wd * DAY;
    end = from + 7 * DAY;
    prevFrom = from - 7 * DAY;
  } else if (period === 'year') {
    from = at(ld.y, 1, 1);
    end = at(ld.y + 1, 1, 1);
    prevFrom = at(ld.y - 1, 1, 1);
  } else {
    from = at(ld.y, ld.m, 1);
    end = at(ld.m === 12 ? ld.y + 1 : ld.y, ld.m === 12 ? 1 : ld.m + 1, 1);
    prevFrom = at(ld.m === 1 ? ld.y - 1 : ld.y, ld.m === 1 ? 12 : ld.m - 1, 1);
  }
  const to = Math.min(end, now);
  const elapsed = to - from;
  return { from, to: end, prev: { from: prevFrom, to: prevFrom + elapsed }, days: Math.max(1, elapsed / DAY) };
}

function priceOf(w: EnergyMockWorld, home: string): { currency: string; price: number } | null {
  const s = w.settings[home];
  const t = s?.tariff_id ? w.tariffs.find((x) => x.tariff_id === s.tariff_id) : undefined;
  if (!t) return null;
  const r = t.versions[t.versions.length - 1].rates ?? {};
  return { currency: t.currency, price: r.flat ?? r.tiers?.[0]?.price ?? r.seasons?.[0]?.periods[0]?.price ?? 3 };
}

function carbonOf(h: MockHomeIn, mwh: number): Carbon {
  return isCN(h)
    ? { kg: (mwh / 1e6) * 0.5703, gas: 'co2', factor_year: 2023, provisional: true, region: '中國', source: '生態環境部' }
    : { kg: (mwh / 1e6) * 0.474, gas: 'co2e', factor_year: 2025, provisional: true, region: '台灣', source: '能源署' };
}

function addMoney(list: Money[], m: Money) {
  const x = list.find((y) => y.currency === m.currency);
  if (x) x.amount += m.amount;
  else list.push({ ...m });
}

/** 範圍 → {leaf}清單（scope：all / org:<id> / site:<id> / home:<id> / view:<id>）。 */
function homesInScope(w: EnergyMockWorld, org: string, scope: string): MockHomeIn[] {
  const inOrg = w.homes.filter((h) => h.org_id === org);
  const [kind, ...rest] = scope.split(':');
  const id = rest.join(':');
  if (kind === 'home') return inOrg.filter((h) => h.home_id === id);
  if (kind === 'site') {
    const hit = inOrg.filter((h) => h.site_id === id);
    return hit.length ? hit : inOrg;
  }
  return inOrg;
}

interface HomeCalc {
  h: MockHomeIn;
  meters: Meter[];
  rows: { m: Meter; cur: ReturnType<typeof meterSpan>; prev: ReturnType<typeof meterSpan> }[];
  mwh: number;
  prevMwh: number;
  missingHours: number;
  missingMeters: number;
  costs: Money[];
  noTariff: boolean;
  unavailable: boolean;
  days: number;
  span: Span;
}

function calcHome(w: EnergyMockWorld, h: MockHomeIn, period: Period, date: string | null, now: number): HomeCalc {
  const meters = metersOf(w, h);
  const sp = periodSpan(period, date, h.tz, now);
  const unavailable = w.unavailableHomes.includes(h.home_id);
  const rows = meters.map((m) => ({ m, cur: meterSpan(m, h.tz, { from: sp.from, to: sp.to }, now), prev: meterSpan(m, h.tz, sp.prev, now) }));
  const mwh = unavailable ? 0 : rows.reduce((a, r) => a + r.cur.mwh, 0);
  const prevMwh = rows.reduce((a, r) => a + r.prev.mwh, 0);
  const missing = rows.filter((r) => r.cur.missing > 0);
  const price = priceOf(w, h.home_id);
  const costs: Money[] = price ? [{ currency: price.currency, amount: (mwh / 1e6) * price.price }] : [];
  return {
    h,
    meters,
    rows,
    mwh,
    prevMwh,
    missingHours: missing.reduce((a, r) => a + r.cur.missing, 0),
    missingMeters: missing.length,
    costs,
    noTariff: !price,
    unavailable,
    days: sp.days,
    span: { from: sp.from, to: sp.to },
  };
}

function cmpOf(cur: number, prev: number): { ratio: number | null; prev_mwh: number } {
  return { ratio: prev >= 1e6 ? (cur - prev) / prev : null, prev_mwh: prev };
}

/** 趨勢：日 = 每小時、週 / 月 = 每日、年 = 每月。 */
function seriesOf(calcs: HomeCalc[], period: Period, now: number, byKind: boolean): SeriesPoint[] {
  if (!calcs.length) return [];
  const tz = calcs[0].h.tz;
  const sp = calcs[0].span;
  const off = tzOffsetMin(tz, now) * 60_000;
  const step = period === 'day' ? HOUR : DAY;
  const out: SeriesPoint[] = [];
  const cells: Span[] = [];
  if (period === 'year') {
    const y = new Date(sp.from + off).getUTCFullYear();
    for (let m = 1; m <= 12; m++) cells.push({ from: Date.UTC(y, m - 1, 1) - off, to: Date.UTC(m === 12 ? y + 1 : y, m === 12 ? 0 : m, 1) - off });
  } else for (let t = sp.from; t < sp.to; t += step) cells.push({ from: t, to: t + step });
  for (const c of cells) {
    if (c.from >= now) {
      out.push({ t: new Date(c.from).toISOString(), mwh: null, quality: 'incomplete', coverage: 0 });
      continue;
    }
    let mwh = 0;
    let missing = 0;
    let total = 0;
    const kinds: Partial<Record<MeterKind, number>> = {};
    for (const hc of calcs) {
      if (hc.unavailable) continue;
      for (const m of hc.meters) {
        const r = meterSpan(m, hc.h.tz, c, now);
        mwh += r.mwh;
        missing += r.missing;
        total += r.hours;
        kinds[m.kind] = (kinds[m.kind] ?? 0) + r.mwh;
      }
    }
    const coverage = total ? Math.max(0, 1 - missing / total) : 0;
    out.push({
      t: new Date(c.from).toISOString(),
      mwh: total && coverage === 0 ? null : mwh,
      quality: coverage >= 1 ? 'measured' : coverage > 0 ? 'estimated' : 'incomplete',
      coverage,
      partial: c.to > now,
      ...(byKind ? { by_kind: kinds } : {}),
    });
  }
  return out;
}

function namesOf(w: EnergyMockWorld, calcs: HomeCalc[]): EnergySummary['names'] {
  const homes: Record<string, { name: string; path?: string; tz?: string; org_id?: string }> = {};
  const devices: Record<string, { name: string; home_id?: string }> = {};
  for (const c of calcs) {
    homes[c.h.home_id] = { name: c.h.name, path: c.h.path, tz: c.h.tz, org_id: c.h.org_id };
    for (const m of c.meters) if (m.device_ref) devices[m.device_ref] = { name: m.name, home_id: c.h.home_id };
  }
  return { homes, orgs: Object.fromEntries(w.orgs.map((o) => [o.org_id, o.name])), devices };
}

function attentionOf(w: EnergyMockWorld, calcs: HomeCalc[], now: number): Attention[] {
  const out: Attention[] = [];
  const gaps: { home_id: string; meters: number }[] = [];
  for (const c of calcs) {
    if (c.unavailable) {
      out.push({ kind: 'home_unavailable', level: 'unk', home_id: c.h.home_id, org_id: c.h.org_id, since: new Date(now - 3 * HOUR).toISOString() });
      continue;
    }
    const r = hash(c.h.home_id);
    if (r < 0.12)
      out.push({
        kind: 'plug_overload',
        level: 'bad',
        home_id: c.h.home_id,
        org_id: c.h.org_id,
        device_ref: c.meters.find((m) => m.kind === 'plug')?.device_ref,
        power_mw: 1_720_000,
        limit_mw: 1_650_000,
        source: 'rated',
        at: new Date(now - 33 * 60_000).toISOString(),
      });
    if (r > 0.3 && r < 0.42) out.push({ kind: 'poe_budget', level: 'high', home_id: c.h.home_id, org_id: c.h.org_id, device_ref: c.meters[0]?.station_ref, allocated_w: 114.4, rated_w: 120, used_w: 43, threshold_pct: 95 });
    const g = c.rows.filter((x) => x.m.gapHours).length;
    if (g) gaps.push({ home_id: c.h.home_id, meters: g });
  }
  if (gaps.length) out.push({ kind: 'meter_gap', level: 'unk', home_id: gaps[0].home_id, meters: gaps.reduce((a, g) => a + g.meters, 0), by_home: gaps, since: new Date(now - 3 * HOUR).toISOString() });
  const rank = { bad: 0, high: 1, warn: 2, unk: 3 };
  return out.sort((a, b) => rank[a.level] - rank[b.level]);
}

function summaryOf(w: EnergyMockWorld, calcs: HomeCalc[], period: Period, now: number, totalHomes: number): EnergySummary {
  const live = calcs.filter((c) => !c.unavailable && c.meters.length);
  const mwh = live.reduce((a, c) => a + c.mwh, 0);
  // 比較：上一期同一組量測點、coverage ≥ 90%（mock：缺口 > 10% 的{leaf}排除）
  const comparable = live.filter((c) => c.missingHours < c.meters.length * c.days * 24 * 0.1);
  const excluded = live.filter((c) => !comparable.includes(c)).map((c) => ({ home_id: c.h.home_id, reason: 'prev_coverage' as const, prev_coverage: 0.84 }));
  const cmpCur = comparable.reduce((a, c) => a + c.mwh, 0);
  const cmpPrev = comparable.reduce((a, c) => a + c.prevMwh, 0);
  const costs: (Money & { homes: number })[] = [];
  const carbon: (Carbon & { homes: number })[] = [];
  for (const c of live) {
    for (const m of c.costs) {
      const x = costs.find((y) => y.currency === m.currency);
      if (x) {
        x.amount += m.amount;
        x.homes++;
      } else costs.push({ ...m, homes: 1 });
    }
    const k = carbonOf(c.h, c.mwh);
    const x = carbon.find((y) => y.region === k.region);
    if (x) {
      x.kg += k.kg;
      x.homes++;
    } else carbon.push({ ...k, homes: 1 });
  }
  const meters = live.reduce((a, c) => a + c.meters.length, 0);
  const incompleteHours = live.reduce((a, c) => a + c.missingHours, 0);
  const sp = calcs[0] ? periodSpan(period, null, calcs[0].h.tz, now) : null;
  const days = sp?.days ?? 1;
  const monthDays = sp ? Math.round((sp.to - sp.from) / DAY) : 30;
  const byHome: HomeRow[] = calcs
    .filter((c) => c.meters.length)
    .map((c) => {
      const ok = comparable.includes(c);
      return {
        home_id: c.h.home_id,
        org_id: c.h.org_id,
        mwh: c.mwh,
        comparison: ok && !c.unavailable ? cmpOf(c.mwh, c.prevMwh) : null,
        ...(ok ? {} : { no_compare_reason: c.unavailable ? ('unavailable' as const) : ('prev_coverage' as const), prev_coverage: 0.84 }),
        daily_avg_mwh: c.mwh / days,
        costs: c.costs,
        no_tariff: c.noTariff,
        carbon: [carbonOf(c.h, c.mwh)],
        meters: c.meters.length,
        unavailable_meters: c.unavailable ? c.meters.length : c.missingMeters,
        incomplete_hours: c.missingHours,
        unavailable: c.unavailable,
      };
    });
  const orgIds = [...new Set(calcs.map((c) => c.h.org_id))];
  const byOrg: OrgRow[] | undefined =
    orgIds.length > 1
      ? orgIds.map((o) => {
          const cs = calcs.filter((c) => c.h.org_id === o);
          const l = cs.filter((c) => !c.unavailable && c.meters.length);
          const cur = l.reduce((a, c) => a + c.mwh, 0);
          const prev = l.reduce((a, c) => a + c.prevMwh, 0);
          const oc: Money[] = [];
          for (const c of l) for (const m of c.costs) addMoney(oc, m);
          return {
            org_id: o,
            homes: cs.length,
            homes_with_meters: cs.filter((c) => c.meters.length).length,
            mwh: cur,
            comparison: cmpOf(cur, prev),
            daily_avg_mwh: cur / days,
            costs: oc,
            no_tariff_homes: cs.filter((c) => c.noTariff && c.meters.length).length,
            meters: l.reduce((a, c) => a + c.meters.length, 0),
            unavailable_meters: l.reduce((a, c) => a + c.missingMeters, 0),
            incomplete_hours: l.reduce((a, c) => a + c.missingHours, 0),
          };
        })
      : undefined;
  return {
    period,
    from: sp ? new Date(sp.from + tzOffsetMin(calcs[0].h.tz, now) * 60_000).toISOString().slice(0, 10) : '',
    to: new Date(now).toISOString().slice(0, 10),
    mwh,
    forecast_mwh: period === 'month' && days >= 3 ? (mwh / days) * monthDays : null,
    cost_by_currency: costs,
    carbon,
    coverage: meters ? 1 - incompleteHours / Math.max(1, meters * days * 24) : 0,
    quality: incompleteHours ? 'estimated' : 'measured',
    incomplete_hours: incompleteHours,
    incomplete_meters: live.reduce((a, c) => a + c.missingMeters, 0),
    meters,
    poe_meters: live.reduce((a, c) => a + c.meters.filter((m) => m.kind === 'poe').length, 0),
    comparison:
      comparable.length && cmpPrev > 0
        ? {
            prev_mwh: cmpPrev,
            cur_mwh: cmpCur,
            ratio: cmpPrev >= 1e6 ? (cmpCur - cmpPrev) / cmpPrev : null,
            meters: comparable.reduce((a, c) => a + c.meters.length, 0),
            homes: comparable.length,
            prev_from: sp ? new Date(sp.prev.from).toISOString().slice(0, 10) : '',
            prev_to: sp ? new Date(sp.prev.to - 1).toISOString().slice(0, 10) : '',
            excluded,
          }
        : null,
    ...(comparable.length ? {} : { no_compare_reason: 'no_prev' as const }),
    homes_without_tariff: live.filter((c) => c.noTariff).map((c) => c.h.home_id),
    unavailable_homes: calcs.filter((c) => c.unavailable).map((c) => ({ home_id: c.h.home_id, since: new Date(now - 3 * HOUR).toISOString(), hours: 3 })),
    visible_homes: calcs.length,
    total_homes: totalHomes,
    homes_with_meters: calcs.filter((c) => c.meters.length).length,
    mixed_tz: new Set(calcs.map((c) => c.h.tz ?? '')).size > 1,
    tz: calcs[0]?.h.tz,
    series: seriesOf(live, period, now, live.length === 1),
    attention: attentionOf(w, calcs, now),
    by_home: byHome,
    by_org: byOrg,
    show_carbon: w.orgSettings[calcs[0]?.h.org_id ?? '']?.show_carbon ?? true,
    names: namesOf(w, calcs),
  };
}

function rankRows(w: EnergyMockWorld, calcs: HomeCalc[], now: number): RankRow[] {
  const rows: RankRow[] = [];
  for (const c of calcs) {
    if (c.unavailable) continue;
    const price = priceOf(w, c.h.home_id);
    for (const r of c.rows) {
      const m = r.m;
      const pm = m.device_ref ? w.powerModes[m.device_ref] : undefined;
      rows.push({
        meter: m.key,
        kind: m.kind,
        home_id: c.h.home_id,
        org_id: c.h.org_id,
        name: m.name,
        station_ref: m.station_ref,
        station_name: m.station_name,
        port: m.port,
        device_ref: m.device_ref || undefined,
        ...(m.camera
          ? {
              power_mode: m.power_mode,
              power_mode_options: [0, 1, 2],
              // 每個{leaf}的第 4 支攝影機（倉庫）沒有控制權限
              can_control: m.port !== 4,
              power_mode_changed_at: pm?.result === 'applied' ? new Date(pm.at).toISOString() : undefined,
              power_mode_effect: pm?.result === 'applied' && now - pm.at >= 7 * DAY ? { before_mwh: r.cur.mwh * 1.22, after_mwh: r.cur.mwh, days: 7 } : undefined,
            }
          : {}),
        mwh: r.cur.mwh,
        avg_mw: r.cur.hours - r.cur.missing > 0 ? (r.cur.mwh / (r.cur.hours - r.cur.missing)) : null,
        max_mw: r.cur.max ? r.cur.max * 1000 : null,
        costs: price ? [{ currency: price.currency, amount: (r.cur.mwh / 1e6) * price.price }] : [],
        comparison: cmpOf(r.cur.mwh, r.prev.mwh),
        quality: r.cur.missing ? 'estimated' : 'measured',
        incomplete_hours: r.cur.missing,
      });
    }
  }
  return rows;
}

function sortRows(rows: RankRow[], sort: string): RankRow[] {
  const [key, dir] = sort.split(':');
  const s = dir === 'asc' ? 1 : -1;
  const v = (r: RankRow): number =>
    key === 'avg' ? (r.avg_mw ?? -1) : key === 'max' ? (r.max_mw ?? -1) : key === 'cmp' ? (r.comparison?.ratio ?? -Infinity) : r.mwh;
  return [...rows].sort((a, b) => s * (v(a) - v(b)) || a.name.localeCompare(b.name));
}

export interface MockResponse {
  status: number;
  body: unknown;
}

const err = (status: number, error: string, message: string, detail?: unknown): MockResponse => ({ status, body: { error, message, ...(detail ? { detail } : {}) } });

/** 主入口：一個 API 請求 → 假回應（不認得的路徑回 null）。 */
export function energyMock(w: EnergyMockWorld, method: string, path: string, q: URLSearchParams, body: unknown, now: number): MockResponse | null {
  let m: RegExpMatchArray | null;
  // CSV 端點 mock 不做（畫面在瀏覽器組，同 API 還沒有 CSV 時的退路）
  if (path.endsWith('.csv')) return err(404, 'NOT_FOUND', 'mock 沒有 CSV 端點');
  const period = (q.get('period') as Period) || 'month';
  const date = q.get('date');
  // ---- 組織範圍 ----
  if ((m = path.match(/^\/v1\/orgs\/([^/]+)\/energy\/(summary|ranking|settings|tariffs|power-mode)$/))) {
    const org = decodeURIComponent(m[1]);
    const kind = m[2];
    const inOrg = w.homes.filter((h) => h.org_id === org);
    const scope = q.get('scope') || 'all';
    if (kind === 'summary' && method === 'GET') {
      const hs = homesInScope(w, org, scope);
      const calcs = hs.map((h) => calcHome(w, h, period, date, now));
      return { status: 200, body: { org_id: org, scope, ...summaryOf(w, calcs, period, now, hs.length) } };
    }
    if (kind === 'ranking' && method === 'GET') {
      const hs = homesInScope(w, org, scope);
      const calcs = hs.map((h) => calcHome(w, h, period, date, now));
      let rows = rankRows(w, calcs, now);
      const k = q.get('kind');
      if (k) rows = rows.filter((r) => r.kind === k);
      const home = q.get('home');
      if (home) rows = rows.filter((r) => r.home_id === home);
      const text = (q.get('q') ?? '').trim();
      if (text) rows = rows.filter((r) => (r.name + (r.station_name ?? '') + (w.homes.find((h) => h.home_id === r.home_id)?.name ?? '')).includes(text));
      if (q.get('group') === 'poe_port') rows = rows.filter((r) => r.kind === 'poe');
      if (q.get('gaps') === '1') rows = rows.filter((r) => r.incomplete_hours > 0 || r.unavailable);
      rows = sortRows(rows, q.get('sort') || 'mwh:desc');
      const start = Number(q.get('cursor') || 0);
      const lim = Math.min(100, Number(q.get('limit') || 100));
      const items = rows.slice(start, start + lim);
      return { status: 200, body: { items, next_cursor: start + lim < rows.length ? String(start + lim) : null, total: rows.length, names: namesOf(w, calcs) } };
    }
    if (kind === 'settings') {
      if (method === 'PUT') w.orgSettings[org] = { ...w.orgSettings[org], ...(body as object), org_id: org, version: (w.orgSettings[org]?.version ?? 0) + 1 };
      return { status: 200, body: w.orgSettings[org] ?? { org_id: org, show_carbon: true, carbon_category: 'grid', version: 1 } };
    }
    if (kind === 'tariffs') {
      if (method === 'GET') return { status: 200, body: { items: w.tariffs.filter((t) => t.org_id === org), names: namesOf(w, inOrg.map((h) => calcHome(w, h, 'day', null, now))) } };
      if (method === 'POST') {
        const b = body as Partial<Tariff> & { effective_from?: string; rates?: Tariff['versions'][0]['rates'] };
        const t: Tariff = {
          tariff_id: `trf-${org}-${++w.seq + 10}`,
          org_id: org,
          name: b.name ?? '',
          kind: b.kind ?? 'flat',
          currency: b.currency ?? 'TWD',
          template_id: b.template_id,
          versions: [{ version: 1, effective_from: b.effective_from ?? ymd(new Date(now).getUTCFullYear(), new Date(now).getUTCMonth() + 1, 1), rates: b.rates }],
          applied_homes: [],
          version: 1,
        };
        if (!t.name.trim()) return err(422, 'ENERGY_TARIFF_INVALID', '方案名稱必填', { field: 'name' });
        w.tariffs.push(t);
        return { status: 201, body: t };
      }
    }
    if (kind === 'power-mode' && method === 'POST') {
      const b = body as { device_refs: string[]; mode: number };
      const results: PowerModeResult[] = b.device_refs.map((ref, i) => {
        const home = w.homes.find((h) => metersOf(w, h).some((x) => x.device_ref === ref));
        const cam = home ? metersOf(w, home).find((x) => x.device_ref === ref) : undefined;
        if (!home || cam?.port === 4) return { device_ref: ref, home_id: home?.home_id ?? '', status: 'failed', error: 'FORBIDDEN' };
        // 第一支立刻套用、其餘等設備（2 秒後套用）
        const result = i === 0 ? 'applied' : 'pending';
        w.powerModes[ref] = { mode: b.mode, at: now, result };
        return { device_ref: ref, home_id: home.home_id, status: result, deadline: new Date(now + 6 * 60_000).toISOString() };
      });
      return { status: 200, body: { results } };
    }
  }
  if ((m = path.match(/^\/v1\/orgs\/([^/]+)\/energy\/power-mode\/status$/)) && method === 'GET') {
    const refs = (q.get('device_refs') ?? '').split(',').filter(Boolean);
    const results = refs.map((ref) => {
      const pm = w.powerModes[ref];
      if (pm && pm.result === 'pending' && now - pm.at > 2000) pm.result = 'applied';
      const home = w.homes.find((h) => metersOf(w, h).some((x) => x.device_ref === ref));
      return { device_ref: ref, home_id: home?.home_id ?? '', status: pm?.result ?? 'failed', deadline: pm ? new Date(pm.at + 6 * 60_000).toISOString() : undefined };
    });
    return { status: 200, body: { results } };
  }
  if ((m = path.match(/^\/v1\/orgs\/([^/]+)\/energy\/tariffs\/([^/]+)(\/versions|\/apply)?$/))) {
    const t = w.tariffs.find((x) => x.tariff_id === decodeURIComponent(m![2]));
    if (!t) return err(404, 'NOT_FOUND', '找不到這個電價方案');
    if (m[3] === '/apply' && method === 'POST') {
      const b = body as { home_ids: string[]; effective_from: string };
      for (const tt of w.tariffs) tt.applied_homes = tt.applied_homes.filter((h) => !b.home_ids.includes(h));
      t.applied_homes.push(...b.home_ids);
      for (const h of b.home_ids) w.settings[h] = { ...w.settings[h], tariff_id: t.tariff_id, tariff_source: 'org', tariff_name: t.name };
      return { status: 200, body: t };
    }
    if (m[3] === '/versions' && method === 'POST') {
      const b = body as { effective_from: string; rates: Tariff['versions'][0]['rates'] };
      const back = (now - Date.parse(b.effective_from)) / DAY;
      if (back > 30) return err(422, 'ENERGY_TARIFF_INVALID', '生效日最多追溯 30 天', { field: 'effective_from', reason: 'retro' });
      t.versions.push({ version: t.versions.length + 1, effective_from: b.effective_from, rates: b.rates });
      t.version = (t.version ?? 1) + 1;
      return { status: 200, body: t };
    }
    if (method === 'DELETE') {
      if (t.applied_homes.length) return err(409, 'ENERGY_TARIFF_IN_USE', '還有{leaf}在用這個方案', { homes: t.applied_homes.length });
      w.tariffs = w.tariffs.filter((x) => x !== t);
      return { status: 204, body: null };
    }
    if (method === 'PUT') {
      Object.assign(t, { name: (body as { name?: string }).name ?? t.name, version: (t.version ?? 1) + 1 });
      return { status: 200, body: t };
    }
    return { status: 200, body: t };
  }
  if ((m = path.match(/^\/v1\/orgs\/([^/]+)\/energy\/reports\/(\d{4}-\d{2})$/)) && method === 'GET') {
    const org = decodeURIComponent(m[1]);
    const month = m[2];
    const hs = homesInScope(w, org, q.get('scope') || 'all');
    const calcs = hs.map((h) => calcHome(w, h, 'month', `${month}-01`, now));
    const s = summaryOf(w, calcs, 'month', now, hs.length);
    const [y, mo] = month.split('-').map(Number);
    const finalizeOn = ymd(mo === 12 ? y + 1 : y, mo === 12 ? 1 : mo + 1, 8);
    const rows = rankRows(w, calcs, now);
    const report: MonthReport = {
      month,
      finalized: Date.parse(finalizeOn + 'T00:00:00+08:00') <= now,
      finalize_on: finalizeOn,
      summary: { ...s },
      by_home: s.by_home.map((h) => ({ ...h, top: rows.filter((r) => r.home_id === h.home_id).sort((a, b) => b.mwh - a.mwh).slice(0, 10) })),
    };
    return { status: 200, body: { ...report, names: s.names } };
  }
  // ---- 單一{leaf} ----
  if ((m = path.match(/^\/v1\/homes\/([^/]+)\/energy\/(summary|series|settings)$/))) {
    const home = w.homes.find((h) => h.home_id === decodeURIComponent(m![1]));
    if (!home) return err(404, 'NOT_FOUND', '找不到這個{leaf}');
    if (m[2] === 'settings') {
      const cur = w.settings[home.home_id];
      if (method === 'PUT') {
        if (!cur.can_edit) return err(403, 'FORBIDDEN', '只有擁有者或管理員能修改');
        const b = body as Partial<EnergySettings> & { version?: number };
        if (b.version !== undefined && b.version !== cur.version) return err(409, 'VERSION_CONFLICT', '別人剛改過，重新整理後再改');
        const next = { ...cur, ...b, alerts: { ...cur.alerts, ...(b.alerts ?? {}) }, version: (cur.version ?? 1) + 1 };
        if (b.tariff_id !== undefined) {
          const t = w.tariffs.find((x) => x.tariff_id === b.tariff_id);
          next.tariff_source = t ? 'home' : 'none';
          next.tariff_name = t?.name;
          for (const tt of w.tariffs) tt.applied_homes = tt.applied_homes.filter((h) => h !== home.home_id);
          if (t) t.applied_homes.push(home.home_id);
        }
        if (b.managed_by_template === null) next.managed_by_template = null;
        w.settings[home.home_id] = next;
      }
      return { status: 200, body: w.settings[home.home_id] };
    }
    const calc = calcHome(w, home, period, date, now);
    if (m[2] === 'summary') {
      const s = summaryOf(w, [calc], period, now, 1);
      return { status: 200, body: { home_id: home.home_id, ...s, meters_detail: rankRows(w, [calc], now) } };
    }
    // series：granularity hour / day，from / to（YYYY-MM-DD），meters 篩量測點
    const from = Date.parse((q.get('from') ?? '') + 'T00:00:00+08:00');
    const to = Date.parse((q.get('to') ?? '') + 'T00:00:00+08:00') + DAY;
    if (!Number.isFinite(from) || !Number.isFinite(to)) return err(400, 'BAD_REQUEST', 'from / to');
    const hourly = q.get('granularity') === 'hour';
    if ((to - from) / DAY > 93 && hourly) return err(400, 'ENERGY_RANGE_TOO_LARGE', '期間太長');
    const only = (q.get('meters') ?? '').split(',').filter(Boolean);
    const ms = only.length ? calc.meters.filter((mm) => only.includes(mm.key)) : calc.meters;
    const step = hourly ? HOUR : DAY;
    const pts: SeriesPoint[] = [];
    for (let t = from; t < to; t += step) {
      if (t >= now) {
        pts.push({ t: new Date(t).toISOString(), mwh: null, quality: 'incomplete', coverage: 0 });
        continue;
      }
      let mwh = 0;
      let miss = 0;
      let tot = 0;
      for (const mm of ms) {
        const r = meterSpan(mm, home.tz, { from: t, to: t + step }, now);
        mwh += r.mwh;
        miss += r.missing;
        tot += r.hours;
      }
      const cov = tot ? 1 - miss / tot : 0;
      pts.push({ t: new Date(t).toISOString(), mwh: tot && cov === 0 ? null : mwh, quality: cov >= 1 ? 'measured' : cov > 0 ? 'estimated' : 'incomplete', coverage: cov, partial: t + step > now });
    }
    return { status: 200, body: { home_id: home.home_id, granularity: hourly ? 'hour' : 'day', points: pts } };
  }
  if ((m = path.match(/^\/v1\/homes\/([^/]+)\/things\/([^/]+)\/energy$/)) && method === 'GET') {
    const home = w.homes.find((h) => h.home_id === decodeURIComponent(m![1]));
    const ref = decodeURIComponent(m[2]);
    if (!home) return err(404, 'NOT_FOUND', '找不到');
    const meters = metersOf(w, home);
    const today = calcHome(w, home, 'day', null, now);
    const month = calcHome(w, home, 'month', null, now);
    const price = priceOf(w, home.home_id);
    const series = (ms: Meter[]) => {
      const pts: SeriesPoint[] = [];
      // 平台用 period（day = 每小時、week / month = 每日）；舊參數 granularity / n 照收
      const per = q.get('period');
      const g = q.get('granularity') || (per === 'day' ? 'hour' : 'day');
      const step = g === 'hour' ? HOUR : DAY;
      const n = Number(q.get('n') || (g === 'hour' ? 24 : per === 'week' ? 7 : 30));
      const off = tzOffsetMin(home.tz, now) * 60_000;
      const end = g === 'hour' ? Math.floor(now / HOUR) * HOUR + HOUR : Math.floor((now + off) / DAY) * DAY - off + DAY;
      for (let t = end - n * step; t < end; t += step) {
        let mwh = 0;
        let miss = 0;
        let tot = 0;
        for (const mm of ms) {
          const r = meterSpan(mm, home.tz, { from: t, to: t + step }, now);
          mwh += r.mwh;
          miss += r.missing;
          tot += r.hours;
        }
        const cov = tot ? 1 - miss / tot : 0;
        pts.push({ t: new Date(t).toISOString(), mwh: tot && cov === 0 ? null : mwh, quality: cov >= 1 ? 'measured' : cov > 0 ? 'estimated' : 'incomplete', coverage: cov, partial: t + step > now });
      }
      return pts;
    };
    // Station：每孔
    const ports = meters.filter((x) => x.station_ref === ref);
    if (ports.length) {
      const used = ports.reduce((a, p) => a + (wattsAt(p, new Date(now + tzOffsetMin(home.tz, now) * 60_000).getUTCHours(), 0, now - HOUR, now) ?? 0), 0);
      const body: DeviceEnergy = {
        device_ref: ref,
        home_id: home.home_id,
        today_mwh: today.rows.filter((r) => r.m.station_ref === ref).reduce((a, r) => a + r.cur.mwh, 0),
        month_mwh: month.rows.filter((r) => r.m.station_ref === ref).reduce((a, r) => a + r.cur.mwh, 0),
        month_costs: [],
        poe: { allocated_w: 114.4, rated_w: 120, used_w: +used.toFixed(1) },
        ports: ports.map((p) => {
          const spark: (number | null)[] = [];
          for (let t = Math.floor(now / HOUR) * HOUR - 23 * HOUR; t <= now; t += HOUR) {
            const r = meterSpan(p, home.tz, { from: t, to: t + HOUR }, now);
            spark.push(r.missing >= r.hours ? null : r.mwh);
          }
          return { port: p.port!, meter: p.key, today_mwh: today.rows.find((r) => r.m.key === p.key)?.cur.mwh ?? null, spark, device_ref: p.device_ref || undefined, name: p.name || undefined };
        }),
      };
      return { status: 200, body };
    }
    const mt = meters.find((x) => x.device_ref === ref);
    if (!mt) return err(404, 'NOT_FOUND', '這台沒有用電資料');
    const tr = today.rows.find((r) => r.m.key === mt.key)!;
    const mr = month.rows.find((r) => r.m.key === mt.key)!;
    const body: DeviceEnergy = {
      device_ref: ref,
      home_id: home.home_id,
      power_mw: (wattsAt(mt, new Date(now + tzOffsetMin(home.tz, now) * 60_000).getUTCHours(), 0, now - HOUR, now) ?? 0) * 1000,
      today_mwh: tr.cur.mwh,
      month_mwh: mr.cur.mwh,
      month_costs: price ? [{ currency: price.currency, amount: (mr.cur.mwh / 1e6) * price.price }] : [],
      no_tariff: !price,
      via_poe: mt.kind === 'poe' ? { station_ref: mt.station_ref!, station_name: mt.station_name, port: mt.port! } : undefined,
      series: series([mt]),
    };
    return { status: 200, body };
  }
  if (path === '/v1/energy/tariff-templates' && method === 'GET') {
    const items: TariffTemplate[] = [
      { template_id: 'tw-taipower-commercial', region: 'TW', name: '台電表燈營業用', kind: 'tiered', currency: 'TWD', rates: { tiers: [{ upto_kwh: 330, price: 3.42 }, { upto_kwh: 700, price: 4.48 }, { upto_kwh: null, price: 6.18 }] } },
      {
        template_id: 'tw-taipower-tou3',
        region: 'TW',
        name: '台電高壓三段式',
        kind: 'tou',
        currency: 'TWD',
        rates: {
          seasons: [
            {
              name: '夏月',
              from: '06-01',
              to: '09-30',
              periods: [
                { name: '尖峰', days: 'weekday', from_h: 16, to_h: 22, price: 6.92 },
                { name: '半尖峰', days: 'weekday', from_h: 9, to_h: 16, price: 4.26 },
                { name: '離峰', days: 'all', from_h: 22, to_h: 9, price: 1.85 },
              ],
            },
          ],
        },
      },
      { template_id: 'cn-xm-commercial', region: 'CN', name: '廈門一般工商業', kind: 'flat', currency: 'CNY', rates: { flat: 0.68 } },
    ];
    const region = q.get('region');
    return { status: 200, body: { items: region ? items.filter((i) => i.region === region) : items } };
  }
  return null;
}

export const MOCK_ORG_SETTINGS = (org: string): OrgEnergySettings => ({ org_id: org, show_carbon: true, carbon_category: 'grid', version: 1 });
